import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import { Box, Chip, Divider } from "@mui/material";
import dayjs from "dayjs";
import { get_schedule_with_pagination } from "../../../config/Worksite/scheduleApis";
import Loader from "../../../Components/Loader/Loader";
import CustomModal from "../../../Components/custom_components/CustomModal/CustomModal";
import { ReportDescription } from "../Schedule/ScheduleDetails/Components/ReportDescription/ReportDescription";
import "./FSECalendarViewStyle.css";
import { getActionColors, getFseCalendarScheduleStyle, getScheduleProgressStyle } from "../../../utils/constants";
import isBetween from "dayjs/plugin/isBetween";
import { handlePaginationEntries } from "../../../utils/pagination";
import FseScheduleSearchFilter from "./FseScheduleSearchFilter";
import FseScheduleSearch from "./FseScheduleSearch";
import CustomLegend from "../../../Components/custom_components/CustomLegend/CustomLegend";

dayjs.extend(isBetween);

const FSECalendarView = () => {

    const calendarRef = useRef(null);

    const [scheduleListing, setScheduleListing] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedDateEvents, setSelectedDateEvents] = useState([]);
    const [open, setOpen] = useState(false);
    const [pagination, setPagination] = useState({ start: 1, end: 0, totalEntries: '' });

    const [filters, setFilters] = useState({
        Survey_from_date: "",
        Survey_end_date: "",
        month: '',
        year: ''
    });

    useEffect(() => {
        if (filters?.Survey_from_date && calendarRef.current) {
            const calendarApi = calendarRef.current.getApi();
            const goToDate = dayjs(filters.Survey_from_date, "DD-MM-YYYY").toDate();
            calendarApi.gotoDate(goToDate);
        }
    }, [filters?.Survey_from_date]);

    //  Fetch schedules based on filters
    const fetchSchedules = useCallback(async () => {
        const params = {
            page: pagination.start,
            // pageItemcount: pagination.totalEntries,
            // Survey_from_date: filters?.Survey_from_date || "",
            // Survey_end_date: filters?.Survey_end_date || "",

            SurveyMonth: filters.month || '',
            SurveyYear: filters.year || ''
        };

        try {
            setIsLoading(true);
            const response = await get_schedule_with_pagination(params);
            if (response?.status === "success") {
                const data = response.data;
                setScheduleListing(data.data || []);
                setPagination(handlePaginationEntries(1, data.to, data.total));
            } else {
                setScheduleListing([]);
            }
        } catch (error) {
            console.error("Error fetching schedules:", error);
            setScheduleListing([]);
        } finally {
            setIsLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        fetchSchedules();
    }, [fetchSchedules]);

    //  Convert API data into FullCalendar events
    const events = useMemo(() => {
        return scheduleListing.map((s) => ({
            // id: s.SD_id,
            // title: s?.employeeNames || s.SD_ScheduleCode,
            // start: dayjs(s.SD_Survey_from_date, "DD-MM-YYYY").toDate(),
            // end: dayjs(s.SD_Survey_end_date, "DD-MM-YYYY").add(1, "day").toDate(),
            // allDay: true,
            //   color: getActionColors(s?.is_cancel == 1 ? 3 : s?.Shedule_process_status).color,

            // extendedProps: s,

            // color: getActionColors(s?.is_cancel == 1 ? 3 : s?.Shedule_process_status).color,
            // color: getFseCalendarScheduleStyle(s?.is_cancel == 1 ? 3 : s?.Shedule_process_status).color,

            // backgroundColor: getFseCalendarScheduleStyle(s?.is_cancel == 1 ? 3 : s?.Shedule_process_status).backgroundColor, // ✅ background color
            // textColor: getFseCalendarScheduleStyle(s?.is_cancel == 1 ? 3 : s?.Shedule_process_status).color, // ✅ text color
            // backgroundColor: getFseCalendarScheduleStyle(s?.is_cancel == 1 ? 3 : s?.Shedule_process_status).backgroundColor,
            // textColor: getFseCalendarScheduleStyle(s?.is_cancel == 1 ? 3 : s?.Shedule_process_status).color, // ✅ text color
            // borderColor: getFseCalendarScheduleStyle(s?.is_cancel == 1 ? 3 : s?.Shedule_process_status).backgroundColor, // ✅ text color



            id: s.SD_id,
            title: s?.employeeNames || s.SD_ScheduleCode,
            start: dayjs(s.SD_Survey_from_date, "DD-MM-YYYY").toDate(),
            end: dayjs(s.SD_Survey_end_date, "DD-MM-YYYY").add(1, "day").toDate(),
            allDay: true,

            //  Always ensure fallback colors
            backgroundColor: getFseCalendarScheduleStyle(s?.is_cancel == 1 ? 3 : s?.Shedule_process_status).backgroundColor || "#E0E0E0",
            borderColor: getFseCalendarScheduleStyle(s?.is_cancel == 1 ? 3 : s?.Shedule_process_status).backgroundColor || "#E0E0E0",
            textColor: getFseCalendarScheduleStyle(s?.is_cancel == 1 ? 3 : s?.Shedule_process_status).color || "#000000",
            extendedProps: s,
        }));
    }, [scheduleListing]);

    //  Date click handler
    const handleDateClick = (info) => {
        const clickedDate = dayjs(info.date).startOf("day");

        const filtered = events.filter((event) => {
            const start = dayjs(event.extendedProps.SD_Survey_from_date, "DD-MM-YYYY").startOf("day");
            const end = dayjs(event.extendedProps.SD_Survey_end_date, "DD-MM-YYYY").endOf("day");
            return clickedDate.isBetween(start, end, null, "[]");
        });

        setSelectedDateEvents(filtered);
        setOpen(true);
    };

    const handleEventClick = (info) => {
        const clickedEvent = info.event.extendedProps;
        setSelectedDateEvents([
            {
                ...info.event,
                color: info.event.backgroundColor,
                extendedProps: clickedEvent,
            },
        ]);
        setOpen(true);
        const closeBtn = document.querySelector(".fc-popover .fc-popover-close");
        if (closeBtn) closeBtn.click();
    };

    const handleClose = () => setOpen(false);

    //  When "Search" clicked from filter
    const handleSearch = (filterValues) => {

        const fromDate = filterValues.Survey_from_date
            ? dayjs(filterValues.Survey_from_date, "DD-MM-YYYY")
            : null;

        const year = fromDate ? fromDate.year() : "";
        const month = fromDate ? fromDate.month() + 1 : ""; // month() is 0-based

        const formattedFilters = {
            Survey_from_date: filterValues.Survey_from_date || "",
            Survey_end_date: filterValues.Survey_end_date || "",
            year,
            month,
        };

        setFilters(formattedFilters);
    };
    return (
        <Box sx={{ padding: 2 }}>
            {/*  Add Search Filter on Top */}
            {/* <FseScheduleSearchFilter
                tableTitle="FSE Calendar"
                searchFilter={handleSearch}
            /> */}
            <FseScheduleSearch
                tableTitle="FSE Calendar"
                searchFilter={handleSearch}
            />

            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "10px" }}>
                <CustomLegend
                    customeLegendItems={[
                        {
                            color: getFseCalendarScheduleStyle(0).backgroundColor,
                            label: "New",
                        },
                        {
                            color: getFseCalendarScheduleStyle(1).backgroundColor,
                            label: "In Progress",
                        },
                        {
                            color: getFseCalendarScheduleStyle(2).backgroundColor,
                            label: "Completed",
                        },
                        {
                            color: getFseCalendarScheduleStyle(3).backgroundColor,
                            label: "Cancelled",
                        },
                    ]}
                />
            </div>
            {isLoading ? (
                <Loader />
            ) : (
                <FullCalendar
                    key={filters.month + "-" + filters.year}  //  re-render only when month/year changes
                    ref={calendarRef}
                    initialDate={
                        filters?.Survey_from_date
                            ? dayjs(filters.Survey_from_date, "DD-MM-YYYY").toDate()
                            : new Date() // fallback to today if no filter
                    }
                    plugins={[dayGridPlugin, interactionPlugin]}
                    initialView="dayGridMonth"
                    headerToolbar={{
                        left: "",
                        center: "title",
                        right: "",
                    }}
                    height="75vh"
                    events={events}

                    showNonCurrentDates={false}  // show extra days from other months 

                    dateClick={handleDateClick}
                    eventClick={handleEventClick}
                    eventDisplay="block"
                    dayMaxEvents={3}
                    eventDidMount={(info) => {
                        info.el.style.cursor = "pointer";
                    }}
                />
            )}

            {/*  Modal for showing schedule details */}
            {open && (
                <CustomModal
                    open={open}
                    handleClose={handleClose}
                    title="Schedule Details"
                    maxWidth="900px"
                    body={
                        selectedDateEvents.length === 0 ? (
                            <Box className="fse-calendar-no-schedule">
                                No schedules on this date.
                            </Box>
                        ) : (
                            <Box className="fse-calendar-container">
                                {selectedDateEvents.map((e) => {
                                    const s = e.extendedProps;
                                    const engineers = s.employeeNames || "No engineers assigned";
                                    return (
                                        <Box
                                            key={s.SD_id}
                                            className="fse-calendar-card"
                                            style={{ "--fse-color": s.color }}
                                        >
                                            <Box className="fse-calendar-header">
                                                <Box className="fse-calendar-header-left">
                                                    <Box className="fse-calendar-title">
                                                        {s.projectName}{" "}
                                                        <span className="fse-calendar-code">
                                                            ({s.SD_ScheduleCode})
                                                        </span>
                                                    </Box>
                                                    <Box className="fse-calendar-site">
                                                        Site:{" "}
                                                        <span className="fse-calendar-site-name">
                                                            {s.worksite?.WD_WorksiteName}
                                                        </span>{" "}
                                                        ({s.worksite?.WD_SiteCode})
                                                    </Box>
                                                </Box>

                                                <Chip
                                                    label={
                                                        s?.is_cancel == 1
                                                            ? `Cancelled`
                                                            : s?.schedule_StatusName || "N/A"
                                                    }
                                                    style={{
                                                        color: getScheduleProgressStyle(
                                                            s?.is_cancel == 1
                                                                ? 3
                                                                : s?.Shedule_process_status
                                                        ).color,
                                                        backgroundColor: getScheduleProgressStyle(
                                                            s?.is_cancel == 1
                                                                ? 3
                                                                : s?.Shedule_process_status
                                                        ).backgroundColor,
                                                    }}
                                                />
                                            </Box>

                                            {s.technology?.TechnologiesTypeName && (
                                                <Chip
                                                    label={s.technology?.TechnologiesTypeName}
                                                    className="fse-calendar-chip"
                                                    style={{
                                                        backgroundColor: s.color,
                                                    }}
                                                />
                                            )}
                                            <Divider className="fse-calendar-divider" />

                                            <Box className="fse-calendar-duration">
                                                <strong>Duration:</strong>{" "}
                                                {s.SD_Survey_from_date} → {s.SD_Survey_end_date}
                                            </Box>

                                            <Box>
                                                <Box className="fse-calendar-engineer-title">
                                                    Assigned Engineers:
                                                </Box>
                                                <Box className="fse-calendar-engineer-list">
                                                    {engineers}
                                                </Box>
                                            </Box>
                                            <Divider className="fse-calendar-divider" />

                                            <ReportDescription
                                                title="Description"
                                                description={
                                                    s?.SD_ScheduleDescription || "N/A"
                                                }
                                            />
                                        </Box>
                                    );
                                })}
                            </Box>
                        )
                    }
                />
            )}
        </Box>
    );
};

export default FSECalendarView;
